import {
  RyftEditDirectorForm,
  type RyftDirectorFormValues,
  type RyftDirectorSubmissionValues,
} from "../components/RyftEditDirectorForm";
import { Card, CardContent, H2 } from "@sellar-io/components";
import { useRef } from "react";
import { useNavigate } from "react-router";
import { Alert } from "~/components/alert/Alert";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING } from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useRyftManagementDirectorCreate } from "../../../hooks/api/v2/ryftManagement/useRyftManagementDirectorCreate";
import { useSellarUser } from "../../../hooks/useSellarUser.shared";
import { RyftNonHostedOnboardingStepRedirect } from "./components/RyftNonHostedOnboardingStepRedirect";

export const RyftNonHostedOnboardingBusinessDirector = () => {
  return <RyftNonHostedOnboardingBusinessDirectorLayout />;
};

const RyftNonHostedOnboardingBusinessDirectorLayout = () => {
  return (
    <RyftNonHostedOnboardingStepRedirect stepId="add-business-director">
      <RyftNonHostedOnboardingBusinessDirectorContent />
    </RyftNonHostedOnboardingStepRedirect>
  );
};

const RyftNonHostedOnboardingBusinessDirectorContent = () => {
  const navigate = useNavigate();
  const { user } = useSellarUser();
  const createDirectorMutation = useRyftManagementDirectorCreate();
  const formContainerRef = useRef<HTMLDivElement>(null);

  const errorMessage =
    apiV2AssertError(createDirectorMutation.error)?.response?.data.message ??
    "Unable to add this director to your Ryft account";

  const handleAddDirector = async (values: RyftDirectorSubmissionValues) => {
    await createDirectorMutation.mutateAsync(
      {
        ...values,
        middleName: values.middleName || undefined,
      },
      {
        onSuccess: () => {
          navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING);
        },
        onError: () => {
          formContainerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        },
      },
    );
  };

  const nameParts = user?.name?.trim().split(" ") ?? [];
  const firstName = nameParts[0] ?? "";
  const lastName = nameParts[1] ?? "";
  const email = user?.email ?? "";

  const initialData: RyftDirectorFormValues = {
    firstName,
    middleName: "",
    lastName,
    email,
    dateOfBirth: "",
    countryOfBirth: "GB",
    gender: "",
    nationalities: ["GB"],
    address: {
      lineOne: "",
      city: "",
      country: "GB",
      postalCode: "",
    },
    phoneNumber: "",
  };

  return (
    <>
      <NavigationBar
        title="Add a director"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={() =>
          navigate(BUSINESS_INTEGRATION_RYFT_NON_HOSTED_ONBOARDING)
        }
      />
      <PageContainer className="mx-auto flex max-w-3xl flex-col gap-6 p-4 md:p-6">
        <header ref={formContainerRef} className="flex flex-col gap-2">
          <H2>Add a director</H2>
          <p className="max-w-2xl text-sm leading-6 text-zinc-600 md:text-base">
            Tell us about a director of your business. Ryft uses these details
            to verify the people responsible for your business.
          </p>
        </header>

        {createDirectorMutation.isError && (
          <Alert type="error" message={errorMessage} className="w-full" />
        )}

        <Card className="flex flex-col border border-zinc-200 bg-white">
          <CardContent className="flex flex-col gap-4 p-4">
            <RyftEditDirectorForm
              mode="create"
              initialData={initialData}
              isSubmitting={createDirectorMutation.isPending}
              onSubmit={handleAddDirector}
            />
          </CardContent>
        </Card>
      </PageContainer>
    </>
  );
};
